import type { RoomState } from '@breakoutlearning/firebase-repository/models/RoomState'
import { RoomStateStatus } from '@breakoutlearning/firebase-repository/models/RoomState'
import type { PublicUser } from '@breakoutlearning/firebase-repository/models/PublicUser'
import { BreakoutUserAvatar } from 'components/breakout/BreakoutUserAvatar'
import { useTranslationTyped } from 'i18n/i18n'
import {
  ScheduledDayOfWeekAtTime,
  ScheduledDayOfMonthOnYear,
} from 'pages/student/assignment/ScheduledTimeParts'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { BreakoutButton } from './design-system/BreakoutButton'
import React from 'react'

type RoomCardContext = 'instructor' | 'student'

export const RoomCard = observer(function RoomCard({
  room,
  roomStateStatus,
  context,
  virtualName,
  bottomContent,
  onClick,
  hideCTA,
}: {
  room: RoomState
  roomStateStatus: RoomStateStatus
  context: RoomCardContext
  virtualName: string
  bottomContent?: React.ReactNode
  onClick?: (e?: React.MouseEvent | React.KeyboardEvent) => void
  hideCTA?: boolean
}) {
  return (
    <BaseRoomCard
      className={classNames('m-0.5', {
        'hover:outline-2 hover:outline-breakout-yellow hover:outline-solid':
          !!onClick,
      })}
      title={room.data.roomStateName ?? virtualName}
      context={context}
      titleOverrideOrRoomStateStatus={{
        status: roomStateStatus,
        titleOverride: undefined,
      }}
      scheduledAt={room.data.scheduledAt ?? undefined}
      users={room.usersSortedByName}
      bottomContent={bottomContent}
      onClick={onClick}
      hideCTA={hideCTA}
      // Stable id so the dialog's focus-restoration (DialogStore.refocusTrigger)
      // can re-find this button by id after the card re-renders on close,
      // instead of focusing a detached, stale node.
      ctaButtonId={`manage-group-button-${room.id}`}
    />
  )
})

const BaseRoomCard = observer(function BaseRoomCard({
  className,
  context,
  title,
  hideCTA,
  subtitle,
  scheduledAt,
  isUnassignedRoom,
  users,
  bottomContent,
  titleOverrideOrRoomStateStatus,
  onClick,
  testId = 'room-card',
  ctaButtonId,
}: {
  className?: string
  context: RoomCardContext
  title?: string
  subtitle?: string
  scheduledAt?: Date
  users: PublicUser[]
  bottomContent?: React.ReactNode
  // we need one of these to be defined to drive the top of the card
  titleOverrideOrRoomStateStatus:
    | { status: RoomStateStatus; titleOverride: undefined }
    | { status: undefined; titleOverride: React.ReactNode }
  onClick?: (e?: React.MouseEvent | React.KeyboardEvent) => void
  isUnassignedRoom?: boolean
  hideCTA?: boolean
  testId?: string
  // Stable id applied to the manage CTA button so the dialog can restore focus
  // to it by id after the card re-renders on close.
  ctaButtonId?: string
}) {
  const { tt } = useTranslationTyped()
  const hasUsers = users.length > 0

  // max display in card
  const MAX_USERS = 8

  // minus one to make space for the "show more" count
  const displayUsers =
    users.length > MAX_USERS ? users.slice(0, MAX_USERS - 1) : users

  const remainingCount = users.length - displayUsers.length

  const showMoreCount = remainingCount > 0

  const { titleOverride, status: roomStateStatus } =
    titleOverrideOrRoomStateStatus

  const scheduledDateInvalid =
    roomStateStatus !== undefined &&
    [
      RoomStateStatus.mustScheduleSuspended,
      RoomStateStatus.mustSchedule,
    ].includes(roomStateStatus)

  const showCTA = context === 'instructor' && !hideCTA

  const content = (
    <>
      {titleOverride}
      {!titleOverride && (
        <div className="flex flex-col">
          <h2 className="h-[18px] self-stretch text-title-small text-nowrap">
            <ScheduledDayOfWeekAtTime
              scheduledAt={scheduledDateInvalid ? undefined : scheduledAt}
              tt={tt}
              placeholder={tt.student_assignment.join_dialog_v2.not_scheduled()}
            />
          </h2>
          <strong className="h-[15px] text-body-small text-on-surface-var">
            <ScheduledDayOfMonthOnYear
              scheduledAt={scheduledDateInvalid ? undefined : scheduledAt}
              placeholder="-"
            />
          </strong>
          <strong
            className="h-[15px] truncate text-label-small"
            data-testid="room-card-name"
          >
            {title ?? ''}
            {subtitle && ` - ${subtitle}`}
          </strong>
        </div>
      )}

      <div className="min-h-[132px]">
        {hasUsers ? (
          <div className="flex flex-wrap gap-x-1">
            {displayUsers.map((user, i) => (
              <div
                key={i}
                style={{ width: `calc(50% - 2px)` }}
                className="my-0.5 flex h-[29px] flex-row items-center gap-1 text-body-medium"
              >
                <BreakoutUserAvatar
                  className="min-h-6 min-w-6"
                  user={user}
                  size="small"
                />
                <div
                  style={{ width: `calc(100% - 28px)` }}
                  className="flex flex-col gap-[-4px]"
                >
                  <strong
                    className="truncate text-body-medium"
                    data-testid="room-card-first-name"
                  >
                    {user.data.firstName}
                  </strong>
                  <strong className="truncate text-body-small text-on-surface-var">
                    {user.data.lastName}
                  </strong>
                </div>
              </div>
            ))}
            {showMoreCount && (
              <div
                style={{ width: `calc(50% - 2px)` }}
                className="my-0.5 flex min-h-[29px] flex-row items-center gap-1 text-body-medium"
              >
                <div className="text-fixed-on-accent-color flex min-h-6 min-w-6 items-center justify-center rounded-full bg-white text-[10px] font-medium">
                  +{remainingCount}
                </div>
                <div
                  style={{ width: `calc(100% - 28px)` }}
                  className="flex flex-col"
                >
                  <strong className="truncate text-body-medium">
                    {tt.instructor_assignment.groups.more_students()}
                  </strong>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="flex h-full items-center justify-center text-center text-body-medium text-on-surface-var">
            <span>{tt.student_assignment.join_dialog_v2.empty_group()}</span>
          </div>
        )}
      </div>
      {(context === 'student' || hideCTA) && (
        <div className="mx-auto mt-auto min-h-[33px] w-full">
          {bottomContent}
        </div>
      )}
      {showCTA && (
        <>
          {/* This div simulates bottomContent - it's necessary to achieve correct height */}
          <div className="min-h-[33px]"></div>
          {/* Cross-fade (not a slide): both layers are absolute inset-0 in the
                same 33px box. The manage button therefore occupies the visible box
                in normal layout - not the clipped lower half of a 66px stack - so
                restoring focus to it when the dialog closes cannot make the
                browser scroll this overflow-hidden window (the prior slide left the
                button's layout position below the fold, and focus-scroll dragged it
                half out of view). */}
          <div className="absolute bottom-3 left-0 h-[33px] w-full overflow-hidden rounded-b-[20px]">
            <div className="absolute inset-0 flex flex-col items-center justify-center transition-opacity duration-250 ease-out group-focus-within/card:opacity-0 group-hover/card:opacity-0 group-focus/card:opacity-0">
              {bottomContent}
            </div>
            <div className="pointer-events-none absolute inset-0 flex items-center px-3 opacity-0 transition-opacity duration-200 ease-out group-focus-within/card:pointer-events-auto group-focus-within/card:opacity-100 group-hover/card:pointer-events-auto group-hover/card:opacity-100 group-focus/card:pointer-events-auto group-focus/card:opacity-100">
              {!isUnassignedRoom && (
                <BreakoutButton
                  id={ctaButtonId}
                  kind="accent"
                  size="small"
                  fullWidth
                  // The reveal wrapper is overflow-hidden, which clips the
                  // default outward focus outline. focus-ring-inside swaps it for
                  // an inset ring so the whole ring stays inside the button box.
                  className="focus-ring-inside"
                  // Stop propagation so clicking the CTA doesn't also trigger
                  // the surrounding card's onClick handler.
                  stopPropagation
                  onClick={onClick}
                >
                  {tt.instructor_assignment.groups.manage_group()}
                </BreakoutButton>
              )}
              {isUnassignedRoom && (
                <BreakoutButton
                  id={ctaButtonId}
                  kind="error"
                  size="small"
                  fullWidth
                  // See note on the manage-group button above: inset ring so the
                  // overflow-hidden reveal wrapper doesn't clip it.
                  className="focus-ring-inside"
                  // Stop propagation so clicking the CTA doesn't also trigger
                  // the surrounding card's onClick handler.
                  stopPropagation
                  onClick={onClick}
                >
                  {tt.instructor_assignment.groups.manage_unassigned()}
                </BreakoutButton>
              )}
            </div>
          </div>
        </>
      )}
    </>
  )

  const cardClassName = classNames(
    'group/card relative flex flex-1 flex-col gap-2 rounded-[20px] bg-surface p-3',
    className,
    context,
    {
      'hover:bg-surface-hover cursor-pointer': onClick !== undefined,
    }
  )

  if (!onClick) {
    return (
      <div
        data-testid={testId}
        role="group"
        aria-label={title}
        className={cardClassName}
      >
        {content}
      </div>
    )
  }

  // With the manage button shown, that button is the keyboard path and a click
  // anywhere on the card is a mouse shortcut for it.
  if (showCTA) {
    return (
      <div
        data-testid={testId}
        role="presentation"
        className={cardClassName}
        onClick={onClick}
      >
        <div
          role="group"
          aria-label={title}
          className="flex flex-1 flex-col gap-2"
        >
          {content}
        </div>
      </div>
    )
  }

  // Read-only: no manage button, so the card itself is the button.
  return (
    <div
      data-testid={testId}
      role="button"
      aria-label={title}
      className={cardClassName}
      onClick={onClick}
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick(e)
        }
      }}
    >
      {content}
    </div>
  )
})

export function UnassignedStudentsCard({
  users,
  bottomContent,
  onClick,
  hideCTA,
}: {
  users: PublicUser[]
  bottomContent?: React.ReactNode
  onClick?: (e?: React.MouseEvent | React.KeyboardEvent) => void
  hideCTA?: boolean
}) {
  const { t, tt } = useTranslationTyped()

  return (
    <BaseRoomCard
      testId="unassigned-students-card"
      context="instructor"
      className={classNames('border-2 border-core-error')}
      isUnassignedRoom
      users={users}
      onClick={onClick}
      bottomContent={bottomContent}
      hideCTA={hideCTA}
      // Only one unassigned card exists, so a fixed id is safe.
      ctaButtonId="manage-unassigned-button"
      titleOverrideOrRoomStateStatus={{
        status: undefined,
        titleOverride: (
          <div className="flex min-h-12 items-center gap-2">
            <TriangleAlertIcon size={24} className="shrink-0 text-core-error" />
            <div className="flex flex-col">
              <h2 className="text-title-small">
                {tt.instructor_assignment.groups.unassigned_students()}
              </h2>
              {/* t instead of tt to take advantage of i18n pluralization */}
              <strong className="h-[15px] text-body-small text-on-surface-var">
                {t('instructor_assignment.groups.unassigned_students_count', {
                  count: users.length,
                })}
              </strong>
            </div>
          </div>
        ),
      }}
    />
  )
}
